import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { b2bRequestsApi, clientsApi, type IntegratedOrder, type IntegratedOrderUpdate } from "@/lib/api";
import { integratedOrderErrorMessage } from "@/lib/integratedOrders";
import { IntegratedOrderProductPicker } from "./IntegratedOrderProductPicker";

interface IntegratedOrderEditSheetProps {
  order: IntegratedOrder | null;
  onClose: () => void;
  /** Chamado depois de salvar (e reintegrar, quando pedido) para a listagem recarregar. */
  onSaved: () => void;
}

const digitsOnly = (value?: string | null) => (value ?? "").replace(/\D/g, "");

/**
 * Correção de um registro em revisão, no mesmo padrão da gestão de cupons (painel lateral, seletor de produtos agrupado
 * e comentário obrigatório). "Salvar" grava a correção e mantém o registro em revisão; "Salvar e reintegrar" também
 * reenvia o registro pelo processo do ERP, que resolve escola e produto de novo (CNPJ/CPF e SKU).
 */
export function IntegratedOrderEditSheet({ order, onClose, onSaved }: IntegratedOrderEditSheetProps) {
  const queryClient = useQueryClient();
  const [cnpjCpf, setCnpjCpf] = useState("");
  const [quantity, setQuantity] = useState("");
  const [productId, setProductId] = useState<number | null>(null);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (!order) return;
    setCnpjCpf(order.cnpjCpf ?? "");
    setQuantity(order.quantity != null ? String(order.quantity) : "");
    setProductId(order.productId ?? null);
    setComment("");
  }, [order]);

  // Ciclo do registro; sem ele (registro que não chegou a resolver o ciclo), o ciclo aberto mais recente,
  // que é o mesmo que o processo do ERP usa.
  const cyclesQuery = useQuery({
    queryKey: ["integrated-orders", "open-cycle"],
    queryFn: async () => (await clientsApi.getCycles()).cycles ?? [],
    enabled: order != null && order.cycleYear == null,
    staleTime: 5 * 60 * 1000,
  });
  const cycleYear = useMemo(() => {
    if (order?.cycleYear != null) return order.cycleYear;
    const years = (cyclesQuery.data ?? []).filter((cycle) => cycle.status === "Open").map((cycle) => cycle.year);
    return years.length > 0 ? Math.max(...years) : null;
  }, [order, cyclesQuery.data]);

  const clientCode = order?.clientCode ?? null;
  const cnpjChanged = digitsOnly(cnpjCpf) !== digitsOnly(order?.cnpjCpf);
  const quantityNumber = Number(quantity);
  const quantityValid = Number.isInteger(quantityNumber) && quantityNumber > 0;
  const cnpjValid = [11, 14].includes(digitsOnly(cnpjCpf).length);
  // Com o CNPJ/CPF alterado, a lista de produtos ainda seria a da escola anterior.
  const canChooseProduct = !cnpjChanged && clientCode != null && cycleYear != null;

  // O comentário é obrigatório sempre que algum campo for corrigido; sem correção, só reintegrar.
  const hasChanges = cnpjChanged
    || (quantityValid && quantityNumber !== order?.quantity)
    || (productId != null && productId !== order?.productId);
  const commentValid = !hasChanges || comment.trim().length > 0;
  const formValid = quantityValid && cnpjValid && commentValid;

  const buildChanges = (): IntegratedOrderUpdate => {
    const changes: IntegratedOrderUpdate = { comment: comment.trim() };
    if (cnpjChanged) changes.cnpjCpf = cnpjCpf.trim();
    if (quantityNumber !== order?.quantity) changes.quantity = quantityNumber;
    if (productId != null && productId !== order?.productId) changes.productId = productId;
    return changes;
  };

  const save = useMutation({
    mutationFn: async (reintegrate: boolean) => {
      if (!order) throw new Error("Registro não selecionado.");
      if (hasChanges) await b2bRequestsApi.update(order.id, buildChanges());
      return reintegrate ? await b2bRequestsApi.reintegrate(order.id) : null;
    },
    onSuccess: (result, reintegrate) => {
      if (!reintegrate) toast.success("Registro corrigido.");
      else if (result?.needsReview) toast.warning(`Continua em revisão: ${result.reviewReason ?? result.message}`);
      else toast.success(result?.message ?? "Registro reintegrado.");
      queryClient.invalidateQueries({ queryKey: ["integrated-orders"] });
      onSaved();
      onClose();
    },
    onError: (error) => toast.error(integratedOrderErrorMessage(error)),
  });

  return (
    <Sheet open={order != null} onOpenChange={(open) => !open && !save.isPending && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[720px]">
        <SheetHeader className="space-y-1 border-b px-6 py-5 pr-12 text-left sm:text-left">
          <SheetTitle className="text-xl font-bold leading-tight">Corrigir registro {order?.id}</SheetTitle>
          <SheetDescription className="text-sm">
            Pré-pedido {order?.preOrderId ?? "—"}. A correção mantém o registro em revisão; use "Salvar e reintegrar" para
            enviá-lo de novo pelo processo do ERP, que confere escola e produto (pelo SKU).
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {order?.reviewReason && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <p className="font-medium text-destructive">Motivo da revisão</p>
              <p className="mt-1">{order.reviewReason}</p>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="integrated-order-cnpj">CNPJ/CPF da escola</Label>
            <Input id="integrated-order-cnpj" value={cnpjCpf} onChange={(event) => setCnpjCpf(event.target.value)} />
            {!cnpjValid && <p className="text-xs text-destructive">Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).</p>}
            {order?.clientName && !cnpjChanged && (
              <p className="text-xs text-muted-foreground">{order.clientName} ({order.clientCode})</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="integrated-order-quantity">Quantidade</Label>
            <Input
              id="integrated-order-quantity"
              type="number"
              min={1}
              step={1}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              className="sm:max-w-[200px]"
            />
            {!quantityValid && <p className="text-xs text-destructive">A quantidade deve ser um inteiro maior que zero.</p>}
          </div>

          <div className="grid gap-2">
            <Label>Produto da escola no ciclo {cycleYear ?? "atual"}</Label>
            {order?.itemId && (
              <p className="text-xs text-muted-foreground">SKU recebido do ERP: {order.itemId}</p>
            )}
            {canChooseProduct ? (
              <IntegratedOrderProductPicker
                clientCode={clientCode!}
                cycleYear={cycleYear!}
                value={productId}
                onChange={setProductId}
                disabled={save.isPending}
              />
            ) : (
              <p className="rounded-[10px] border border-dashed px-4 py-5 text-sm text-muted-foreground">
                {clientCode == null
                  ? "A escola ainda não foi identificada. Salve o CNPJ/CPF para identificá-la; depois reabra para escolher o produto."
                  : cnpjChanged
                    ? "Com o CNPJ/CPF alterado, o SKU recebido é revalidado na reintegração para a escola nova. Para trocar o produto, salve o CNPJ/CPF e reabra."
                    : "Carregando o ciclo..."}
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="integrated-order-comment">Comentário da correção{hasChanges ? " (obrigatório)" : ""}</Label>
            <Textarea
              id="integrated-order-comment"
              rows={3}
              maxLength={1000}
              placeholder="Explique o que foi corrigido e por quê"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
            {!commentValid && <p className="text-xs text-destructive">Informe o comentário da correção.</p>}
            {order?.reviewedBy && (
              <p className="text-xs text-muted-foreground">
                Última revisão por {order.reviewedBy}{order.reviewComment ? `: ${order.reviewComment}` : ""}
              </p>
            )}
          </div>
        </div>

        <SheetFooter className="items-center gap-2 border-t px-6 py-4 sm:justify-end sm:space-x-0">
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>Cancelar</Button>
          <Button variant="secondary" onClick={() => save.mutate(false)} disabled={save.isPending || !formValid || !hasChanges}>
            Salvar
          </Button>
          <Button onClick={() => save.mutate(true)} disabled={save.isPending || !formValid}>
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar e reintegrar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
